import { Middleware } from "@reduxjs/toolkit";
import { alert, customerAnalytics } from "../../../utils/APICalls/APICalls";
import {
  DEFAULT_RETRY,
  NETWORK_ERROR_RETRY,
  isNetworkErroFunc,
} from "../../../utils/calculateCartHepler";
import { normalizeAddPayload } from "../../../utils/cart/cartLineTypes";
import { getCookie } from "../../../utils/CookieHelper";
import { COOKIE_ENUM } from "../../../utils/enums";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import {
  FranchiseDeliveryConfig,
  PAGER_DUTY_ERROR_URGENCY,
} from "../../../utils/types";
import { AppDispatch, RootState } from "../app/store";
import {
  calculateCartWithOffersCall,
  calculateOrderCartV2Call,
  triggerCartRecalculate,
} from "../http/cartAndLoyaltyV2";
import { selectUseLoyaltyService } from "../selectors/loyaltyServiceSelectors";
import { setPendingAppliedOfferAnalyticsEvent } from "../slices/cartLoyaltyV2Slice";
import { mergeMenuLookupsFromCartLineInput } from "../slices/configSlice";

let recalculateScheduled = false;

/** Coalesce rapid sync cart mutations (e.g. remove + redeem) into one calculate. */
function scheduleCartRecalculate(dispatch: AppDispatch): void {
  if (recalculateScheduled) {
    return;
  }
  recalculateScheduled = true;
  queueMicrotask(() => {
    recalculateScheduled = false;
    dispatch(triggerCartRecalculate());
  });
}

function shouldTriggerRecalculate(
  action: { type: string; payload?: unknown },
  state: RootState
): boolean {
  if (
    action.type === "cartLoyaltyV2/redeemOfferAction" &&
    state.cartLoyaltyV2Reducer.redirectToItem &&
    (state.cartLoyaltyV2Reducer.redirectToItem.itemId !== undefined ||
      !!state.cartLoyaltyV2Reducer.redirectToItem.itemFromListOffer)
  ) {
    return false;
  }

  if (
    action.type === "cartLoyaltyV2/redeemLoyaltyOffer" &&
    (action.payload as { applicationLevel?: string })?.applicationLevel ===
      "ITEM"
  ) {
    return false;
  }

  const loyaltyRedirect =
    state.cartLoyaltyV2Reducer.loyalty?.applyFlow?.redirect;
  if (
    action.type === "cartLoyaltyV2/applyLoyaltyItemOffer" &&
    (loyaltyRedirect?.loyaltyItemFromListOffer !== undefined ||
      loyaltyRedirect?.itemId !== undefined)
  ) {
    return false;
  }

  return true;
}

function hasCartContentForRetry(state: RootState): boolean {
  if (selectUseLoyaltyService(state)) {
    return (state.cartLoyaltyV2Reducer.loyalty?.lines?.length ?? 0) > 0;
  }

  return state.cartLoyaltyV2Reducer.cartActions.length > 0;
}

let pendingRemovalOperation = false;

const CART_MUTATION_ACTIONS = new Set([
  "cartLoyaltyV2/addItemAction",
  "cartLoyaltyV2/removeItemAction",
  "cartLoyaltyV2/editItemAction",
  "cartLoyaltyV2/editLineQuantityAction",
  "cartLoyaltyV2/applyLoyaltyItemOffer",
]);

function injectUseLoyaltyService(
  action: { type: string; payload?: unknown },
  useLoyaltyService: boolean
) {
  if (!action.payload || typeof action.payload !== "object") {
    return action;
  }

  return {
    ...action,
    payload: { ...(action.payload as object), useLoyaltyService },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Ported from main (#160): the alert payload used to be a template string
// containing the whole cart, which pushed customer data into Sentry and
// PagerDuty. Structured fields only.
const getCartFailureAlertData = (
  error: { code?: string; message?: string } | undefined,
  retryCount: number,
  networkError: boolean,
  rateLimited: boolean,
  cartVersion?: number
) => ({
  cause: error?.message || "Cart calculation failed",
  retryCount,
  networkError,
  rateLimited,
  status: rateLimited
    ? "rate_limited"
    : networkError
    ? "network_error"
    : "calculation_error",
  ...(error?.code ? { errorCode: error.code } : {}),
  ...(cartVersion !== undefined ? { cartVersion } : {}),
});

export const cartMiddleware: Middleware =
  (store) => (next) => (action: any) => {
    if (action.type === "cartLoyaltyV2/removeItemAction") {
      if (pendingRemovalOperation) {
        return;
      }

      pendingRemovalOperation = true;

      // Set a timeout to reset the flag
      setTimeout(() => {
        pendingRemovalOperation = false;
      }, 100); // Adjust this timeout as needed
    }

    // Check if the action is one of the cart actions
    const cartActions = [
      "cartLoyaltyV2/addItemAction",
      "cartLoyaltyV2/removeItemAction",
      "cartLoyaltyV2/editItemAction",
      "cartLoyaltyV2/editLineQuantityAction",
      "cartLoyaltyV2/deliveryFeesAction",
      "cartLoyaltyV2/resetCartAction",
      "cartLoyaltyV2/redeemOfferAction",
      "cartLoyaltyV2/removeOfferAction",
      "cartLoyaltyV2/redeemLoyaltyOffer",
      "cartLoyaltyV2/removeLoyaltyOffer",
      "cartLoyaltyV2/applyPayWithPoints",
      "cartLoyaltyV2/removePayWithPoints",
      "cartLoyaltyV2/applyLoyaltyItemOffer",
      "cartLoyaltyV2/receiptErrorAction",
      "queryParams/setQueryParam",
      "delivery/setCurrentDeliveryConfig",
      "delivery/setDeliveryMinMaxFees",
    ];

    const oldState = store.getState() as RootState;

    const useLoyaltyService = selectUseLoyaltyService(oldState);
    const actionForReducer = CART_MUTATION_ACTIONS.has(action.type)
      ? injectUseLoyaltyService(action, useLoyaltyService)
      : action;

    if (
      useLoyaltyService &&
      (action.type === "cartLoyaltyV2/addItemAction" ||
        action.type === "cartLoyaltyV2/editItemAction" ||
        action.type === "cartLoyaltyV2/applyLoyaltyItemOffer") &&
      actionForReducer.payload &&
      typeof actionForReducer.payload === "object"
    ) {
      const normalized = normalizeAddPayload(actionForReducer.payload);
      if (normalized.subItemDetails && normalized.itemDetails) {
        (store.dispatch as AppDispatch)(
          mergeMenuLookupsFromCartLineInput({
            subItemDetails: normalized.subItemDetails,
            itemDetails: normalized.itemDetails,
            version: normalized.version,
          })
        );
      }
    }

    const result = next(actionForReducer);
    if (action.type === "delivery/setCurrentDeliveryConfig") {
      const currentDeliveryFees = oldState.delivery.deliveryFees;
      const newDeliveryFees = (action.payload as FranchiseDeliveryConfig | null)
        ?.deliveryFees;

      // Skip calculation if the delivery config hasn't actually changed
      if (currentDeliveryFees === newDeliveryFees) {
        return result;
      }
    }

    // ADDING RETRY LOGIC
    // Check if this is a rejected calculateCartWithOffersCall action
    if (action.type === "calculateCartWithOffersCall/rejected") {
      const error = action.error;
      const meta = action.meta;
      const currentRetryCount = meta?.arg?.retryCount || 0;

      // Parse error message to determine type
      const errMsg = error?.message || "";
      const isNetworkError = isNetworkErroFunc(errMsg);
      // A 429 (rate limited) must never be retried — retrying immediately just
      // amplifies the storm against the backend rate limiter and keeps a looping
      // client hammering /cart/calculate.
      const isRateLimited = errMsg.includes("429");

      if (!isNetworkError || currentRetryCount >= NETWORK_ERROR_RETRY) {
        store.dispatch(setPendingAppliedOfferAnalyticsEvent(undefined));
      }

      // Get the current state to access cart actions
      const state = store.getState() as RootState;
      // Handle network errors
      if (isNetworkError) {
        if (currentRetryCount < NETWORK_ERROR_RETRY) {
          const errorEnum =
            currentRetryCount < DEFAULT_RETRY
              ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
              : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

          alert(
            `${errorEnum}`,
            getCartFailureAlertData(
              error,
              currentRetryCount,
              isNetworkError,
              isRateLimited
            ),
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError
          );
          (store.dispatch as AppDispatch)(
            calculateCartWithOffersCall({ retryCount: currentRetryCount + 1 })
          );
        } else {
          // Max network retries reached, show alert
          alert(
            `${AlertErrorType.Error_CalculateCartCall_After_3_FailedNetworkCalls}`,
            getCartFailureAlertData(
              error,
              currentRetryCount,
              isNetworkError,
              isRateLimited
            ),
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError
          );
        }
      } else {
        // Non-network errors
        const errorEnum =
          currentRetryCount < DEFAULT_RETRY
            ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
            : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

        alert(
          `${errorEnum}`,
          getCartFailureAlertData(
            error,
            currentRetryCount,
            isNetworkError,
            isRateLimited
          ),
          PAGER_DUTY_ERROR_URGENCY.HIGH,
          true
        );

        // Then check if we should retry (never on a 429 — see isRateLimited).
        if (currentRetryCount < DEFAULT_RETRY && !isRateLimited) {
          // Note: The reducer will have popped the action already
          // Only retry if we still have actions
          if (hasCartContentForRetry(state)) {
            (store.dispatch as AppDispatch)(
              calculateCartWithOffersCall({ retryCount: currentRetryCount + 1 })
            );
          }
          // If no actions left, the reducer will reset the cart
        }
        // If max retries reached, the reducer will reset the cart
      }

      return result;
    }

    // Handle rejected calculateOrderCartV2Call — mirrors the existing handler above
    if (action.type === "calculateOrderCartV2Call/rejected") {
      // Aborted requests are silent — no retry, no alert.
      // Axios 0.x uses message="canceled"; Axios 1.x adds code="ERR_CANCELED".
      if (
        action.error?.code === "ERR_CANCELED" ||
        action.error?.message === "canceled"
      ) {
        return result;
      }

      const error = action.error;
      const meta = action.meta;
      const currentRetryCount = meta?.arg?.retryCount || 0;
      const cartVersion = meta?.arg?.cartVersion;

      const errMsg = error?.message || "";
      const isNetworkError = isNetworkErroFunc(errMsg);
      // Never retry a 429 (rate limited) — see the calculateCartWithOffersCall
      // handler above.
      const isRateLimited = errMsg.includes("429");

      const state = store.getState() as RootState;

      if (isNetworkError) {
        if (currentRetryCount < NETWORK_ERROR_RETRY) {
          const errorEnum =
            currentRetryCount < DEFAULT_RETRY
              ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
              : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

          alert(
            `${errorEnum}`,
            getCartFailureAlertData(
              error,
              currentRetryCount,
              isNetworkError,
              isRateLimited
            ),
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError
          );
          (store.dispatch as AppDispatch)(
            calculateOrderCartV2Call({
              retryCount: currentRetryCount + 1,
              cartVersion,
            })
          );
        } else {
          alert(
            `${AlertErrorType.Error_CalculateCartCall_After_3_FailedNetworkCalls}`,
            getCartFailureAlertData(
              error,
              currentRetryCount,
              isNetworkError,
              isRateLimited
            ),
            PAGER_DUTY_ERROR_URGENCY.HIGH,
            isNetworkError
          );
        }
      } else {
        const errorEnum =
          currentRetryCount < DEFAULT_RETRY
            ? AlertErrorType.ErrorCalculateCartCall_POP_ONE
            : AlertErrorType.ErrorCalculateCartCall_Clear_Cart;

        alert(
          `${errorEnum}`,
          getCartFailureAlertData(
            error,
            currentRetryCount,
            isNetworkError,
            isRateLimited
          ),
          PAGER_DUTY_ERROR_URGENCY.HIGH,
          true
        );

        if (
          currentRetryCount < DEFAULT_RETRY &&
          !isRateLimited &&
          hasCartContentForRetry(state)
        ) {
          (store.dispatch as AppDispatch)(
            calculateOrderCartV2Call({
              retryCount: currentRetryCount + 1,
              cartVersion,
            })
          );
        }
      }

      return result;
    }

    // Original middleware logic for regular cart actions
    const token = getCookie(COOKIE_ENUM.TOKEN);
    const state = store.getState() as RootState;

    if (cartActions.includes(action.type) && token) {
      if (
        action.type === "cartLoyaltyV2/redeemOfferAction" &&
        state.cartLoyaltyV2Reducer.redirectToItem &&
        (state.cartLoyaltyV2Reducer.redirectToItem.itemId !== undefined ||
          !!state.cartLoyaltyV2Reducer.redirectToItem.itemFromListOffer)
      ) {
        return result;
      }

      // applyLoyaltyItemOffer set a redirect (item-from-list or addon item page).
      // Skip calculate — it fires after the user picks an item or saves addons.
      if (shouldTriggerRecalculate(action, state)) {
        scheduleCartRecalculate(store.dispatch as AppDispatch);
      }
    }

    if (
      (action.type === "calculateCartWithOffersCall/fulfilled" ||
        action.type === "calculateOrderCartV2Call/fulfilled") &&
      token
    ) {
      const latestState = store.getState() as RootState;
      const pendingApplyAnalytics =
        latestState.cartLoyaltyV2Reducer.pendingAppliedOfferAnalytics;
      const selectedFranchise = latestState.config.selectedFranchise;
      const restaurantId = latestState.config.restaurantInfo?.id;

      if (pendingApplyAnalytics?.event && selectedFranchise?.slug) {
        customerAnalytics(
          pendingApplyAnalytics.event,
          pendingApplyAnalytics.extra,
          selectedFranchise.id,
          restaurantId,
          selectedFranchise.slug
        ).catch((e) =>
          console.debug(
            e,
            "Couldn't add the customer analysis " +
              pendingApplyAnalytics.event +
              " to db"
          )
        );
        store.dispatch(setPendingAppliedOfferAnalyticsEvent(undefined));
      }
    }

    return result;
  };
